import {
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react'
import { Chart, registerables } from 'chart.js'
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FileInput } from '../../../components/FileInput'
import { Icon } from '../../../components/Icon'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { SectionCard } from '../../../components/SectionCard'
import { NOT_APPLICABLE, StarRating } from '../../../components/StarRating'
import { useAppToast } from '../../../helpers'
import { IBrilhaMagentaAnswer } from '../../../interfaces/BrilhaMagenta/IBrilhaMagentaAnswer'
import BrilhaMagentaService from '../../../services/BrilhaMagenta/BrilhaMagentaService'

Chart.register(...registerables)

/** Step dos dois registros do gestor, que a IA pode sugerir a partir da transcrição. */
const REGISTRATION_STEP = 2
const TRANSCRIPT_MAX_SIZE_MB = 2

interface IFormField {
  id: number
  title: string
  type: string
  required: boolean
  options: string
  groups: string
}

export default function BrilhaMagentaStepPage() {
  const toast = useAppToast()
  const brilhaMagentaService = new BrilhaMagentaService()
  const navigate = useNavigate()
  const id = parseInt(useParams().id as string)
  const [form, setForm] = useState<IFormField[]>([])
  const [formTitle, setFormTitle] = useState<string>('')
  const [formAnswers, setFormAnswers] = useState<IBrilhaMagentaAnswer>({})
  const [saving, setSaving] = useState<boolean>(false)
  const [step, setStep] = useState<number>(0)
  const [isLastStep, setIsLastStep] = useState<boolean>(false)
  const [transcript, setTranscript] = useState<File | null>(null)
  const [pendingTranscript, setPendingTranscript] = useState<File | null>(null)
  const [suggesting, setSuggesting] = useState<boolean>(false)

  const suggestFromTranscript = async (file: File) => {
    setSuggesting(true)
    try {
      const { answers } = await brilhaMagentaService.suggestFromTranscript(id, file)
      setFormAnswers((prev) => ({ ...prev, ...answers }))
      toast.info('Sugestão gerada! Revise os textos antes de finalizar.')
    } catch (error) {
      toast.error('Não foi possível gerar a sugestão', error)
    } finally {
      setSuggesting(false)
    }
  }

  // Runs as soon as the file is picked; text already written is only replaced after a confirm.
  const handleTranscriptChange = (file: File | null) => {
    setTranscript(file)
    if (!file) return

    // The API only suggests text for the step's textareas.
    const hasText = form.some((field) => field.type === 'textarea' && formAnswers[field.id]?.trim())
    if (hasText) {
      setPendingTranscript(file)
      return
    }

    suggestFromTranscript(file)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)

    // valida se todos os campos foram preenchidos
    let hasEmptyField = false
    form.map((field) => {
      if (field.type === 'title') return

      if (!formAnswers[field.id] || (formAnswers[field.id] === '0' && field.type === 'stars')) {
        hasEmptyField = true
        setSaving(false)
        return
      }
    })

    if (hasEmptyField) {
      toast.invalidForm('Preencha todos os campos antes de continuar.')
      return
    }

    brilhaMagentaService.update(id, formAnswers).then((response) => {
      if (response.data.success) {
        loadStep()
      }

      setSaving(false)
    })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step])

  const loadStep = async () => {
    brilhaMagentaService.getStep(id).then((response) => {
      setFormAnswers({})
      setForm([])
      setStep(response.data.step)

      if (response.data.finished) {
        navigate('/brilha-magenta/complete')

        return
      }

      setIsLastStep(!!response.data.is_last_step)
      setFormTitle(response.data.form.name)
      setForm(response.data.questions)
    })
  }

  useEffect(() => {
    loadStep()
  }, [])

  return (
    <PublicFormLayout title="Brilha Magenta">
      <Heading as="h2" mb="4">
        {formTitle}
      </Heading>
      <form onSubmit={handleSubmit}>
        {step === REGISTRATION_STEP && (
          <SectionCard variant="tile" title="Preencher com IA" titleAs="h3" mb="4">
            <Text variant="caption" mb="3">
              Envie a transcrição da conversa para a IA sugerir os registros abaixo. Revise e edite o texto antes de
              finalizar.
            </Text>
            <FileInput
              accept="transcript"
              maxSizeMB={TRANSCRIPT_MAX_SIZE_MB}
              label="Transcrição da conversa"
              value={transcript}
              onChange={handleTranscriptChange}
              isDisabled={suggesting}
              helperText={
                suggesting ? (
                  <Flex align="center" gap="2" color="gray.500">
                    <Spinner size="xs" /> Analisando transcrição...
                  </Flex>
                ) : undefined
              }
            />
          </SectionCard>
        )}

        {form.map((field) => {
          return (
            <FormControl isRequired={field.type !== 'title'} key={field.id} mb="4">
              {field.title.trim() && <FormLabel>{field.title}</FormLabel>}
              {field.type === 'textarea' && (
                <Textarea
                  value={formAnswers[field.id]}
                  onChange={(event) => setFormAnswers({ ...formAnswers, [field.id]: event.target.value })}
                  isDisabled={suggesting}
                  minH="320px"
                />
              )}
              {field.type === 'date' && (
                <Input
                  type="date"
                  value={formAnswers[field.id]}
                  onChange={(event) => setFormAnswers({ ...formAnswers, [field.id]: event.target.value })}
                />
              )}
              {field.type === 'options' && (
                <RadioGroup
                  value={formAnswers[field.id]}
                  onChange={(value) => setFormAnswers({ ...formAnswers, [field.id]: value })}
                >
                  <Stack direction="column" spacing={3}>
                    {(JSON.parse(field.options) as string[]).map((option, index) => (
                      <Radio value={(index + 1).toString()} key={index}>
                        {option}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              )}
              {field.type === 'checkbox' && (
                <CheckboxGroup
                  value={formAnswers[field.id] ? JSON.parse(formAnswers[field.id]) : []}
                  onChange={(value) => setFormAnswers({ ...formAnswers, [field.id]: JSON.stringify(value) })}
                >
                  {Array.isArray(JSON.parse(field.options)) ? (
                    (JSON.parse(field.options) as string[]).map((option, index) => (
                      <Checkbox value={(index + 1).toString()} key={index} marginLeft="2">
                        {option}
                      </Checkbox>
                    ))
                  ) : (
                    <Checkbox value={(1).toString()} marginLeft="2">
                      {JSON.parse(field.options)}
                    </Checkbox>
                  )}
                </CheckboxGroup>
              )}
              {field.type === 'stars' && (
                <Flex align="center" justify="space-between" gap="3">
                  <StarRating
                    label={field.title}
                    value={parseInt(formAnswers[field.id] ?? '0')}
                    onChange={(value) => setFormAnswers({ ...formAnswers, [field.id]: value.toString() })}
                  />
                  {!field.required && (
                    <Button
                      colorScheme="gray"
                      isActive={formAnswers[field.id] === String(NOT_APPLICABLE)}
                      type="button"
                      size={'xs'}
                      onClick={() =>
                        setFormAnswers({
                          ...formAnswers,
                          [field.id]: formAnswers[field.id] === String(NOT_APPLICABLE) ? '0' : String(NOT_APPLICABLE)
                        })
                      }
                    >
                      Não se aplica
                    </Button>
                  )}
                </Flex>
              )}
            </FormControl>
          )
        })}

        {!isLastStep ? (
          <Button colorScheme="magenta" type="submit" disabled={saving || suggesting}>
            Próximo passo &nbsp;
            <Icon name="lni lni-arrow-right" />
          </Button>
        ) : (
          <Button colorScheme="magenta" type="submit" disabled={saving || suggesting}>
            Finalizar Brilha &nbsp;
            <Icon name="lni lni-checkmark" />
          </Button>
        )}
      </form>

      <ConfirmDialog
        isOpen={pendingTranscript !== null}
        onClose={() => {
          setPendingTranscript(null)
          setTranscript(null)
        }}
        // Not returning the promise on purpose: the dialog closes at once and the field shows the progress.
        onConfirm={() => {
          const file = pendingTranscript
          setPendingTranscript(null)
          if (file) suggestFromTranscript(file)
        }}
        action="save"
        title="Substituir o texto atual?"
        description="Os registros já preenchidos serão substituídos pela sugestão da IA."
        confirmLabel="Substituir"
      />
    </PublicFormLayout>
  )
}
